import { jsPDF } from "jspdf";

const BRAND_GREEN = [0, 107, 63];
const DARK = [15, 23, 42];
const MUTED = [107, 114, 128];

// jsPDF's built-in fonts are Latin-1 only, so em dashes and smart quotes from
// the on-screen copy would render as noise.
const toPdfText = (value) =>
  String(value || "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const buildResultFileName = (examTitle) => {
  const exam = String(examTitle || "result")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `ots-${exam || "result"}-result.pdf`;
};

/**
 * Draws the result report field by field instead of rasterising the DOM: it keeps
 * the file small, the text selectable, and works regardless of screen size.
 */
export async function buildResultPdf({ summary, subjects, studentName }) {
  const subjectRows = Array.isArray(subjects) ? subjects : [];
  const doc = new jsPDF({ unit: "pt", format: "a4" });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 48;
  const contentWidth = pageWidth - margin * 2;

  doc.setFillColor(...BRAND_GREEN);
  doc.rect(0, 0, pageWidth, 96, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("One Time Success", margin, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.text(
    toPdfText(
      [summary.examTitle, summary.assessmentTitle].filter(Boolean).join(" - ")
    ),
    margin,
    66
  );
  doc.setFontSize(10);
  doc.text(toPdfText(summary.dateLabel), margin, 84);

  let y = 136;

  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(toPdfText(studentName ? `${studentName}'s result` : "Result summary"), margin, y);

  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.text(toPdfText(summary.encouragement), margin, y);

  y += 28;
  doc.setDrawColor(...BRAND_GREEN);
  doc.setLineWidth(1);
  doc.roundedRect(margin, y, contentWidth, 92, 8, 8, "S");

  const scoreLabel = summary.isJambMock
    ? `${summary.scoreValue}/400`
    : `${summary.scoreValue}/${summary.totalQuestions}`;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...DARK);
  doc.text(scoreLabel, margin + 24, y + 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.text(summary.isJambMock ? "Composite mock score" : "Questions correct", margin + 24, y + 72);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...DARK);
  doc.text(`${summary.accuracy}%`, margin + contentWidth / 2 + 24, y + 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(...MUTED);
  doc.text("Accuracy", margin + contentWidth / 2 + 24, y + 72);

  y += 124;

  const rows = [
    ["Test completion", `${summary.completion}%`],
    ["Total questions", `${summary.totalQuestions}`],
    ["Attempted", `${summary.attempted}`],
    ["Correct", `${summary.correct}`],
    ["Incorrect", `${summary.incorrect}`],
    ["Unanswered", `${summary.unanswered}`],
    ["Time used", `${summary.usedLabel} of ${summary.totalLabel}`],
  ];

  doc.setFontSize(11);
  rows.forEach(([label, value], index) => {
    const rowY = y + index * 24;
    if (index % 2 === 0) {
      doc.setFillColor(249, 250, 251);
      doc.rect(margin, rowY - 13, contentWidth, 24, "F");
    }
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(toPdfText(label), margin + 16, rowY + 3);
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.text(toPdfText(value), margin + contentWidth - 16, rowY + 3, { align: "right" });
  });

  y += rows.length * 24 + 30;

  if (subjectRows.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...DARK);
    doc.text("Subject breakdown", margin, y);
    y += 20;

    const columns = [
      { label: "Subject", x: margin + 16, align: "left" },
      { label: "Correct", x: margin + contentWidth - 250, align: "right" },
      { label: "Wrong", x: margin + contentWidth - 170, align: "right" },
      { label: "Skipped", x: margin + contentWidth - 90, align: "right" },
      { label: "Accuracy", x: margin + contentWidth - 16, align: "right" },
    ];

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    columns.forEach((column) => {
      doc.text(column.label, column.x, y, { align: column.align });
    });
    y += 6;
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, y, margin + contentWidth, y);
    y += 16;

    doc.setFontSize(11);
    subjectRows.forEach((subject) => {
      if (y > pageHeight - 90) {
        doc.addPage();
        y = margin + 20;
      }
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...DARK);
      doc.text(toPdfText(subject.title).slice(0, 32), columns[0].x, y, { align: "left" });
      doc.text(`${subject.correct}`, columns[1].x, y, { align: "right" });
      doc.text(`${subject.incorrect}`, columns[2].x, y, { align: "right" });
      doc.text(`${subject.unanswered}`, columns[3].x, y, { align: "right" });
      doc.setFont("helvetica", "bold");
      doc.text(`${subject.accuracy}%`, columns[4].x, y, { align: "right" });
      y += 22;
    });

    y += 8;
  }

  if (summary.advice && summary.advice.title) {
    if (y > pageHeight - 140) {
      doc.addPage();
      y = margin + 20;
    }
    doc.setFillColor(240, 253, 244);
    doc.roundedRect(margin, y, contentWidth, 84, 8, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BRAND_GREEN);
    doc.text(toPdfText(summary.advice.title), margin + 20, y + 26);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    const adviceLines = doc.splitTextToSize(
      toPdfText(summary.advice.description),
      contentWidth - 40
    );
    doc.text(adviceLines.slice(0, 3), margin + 20, y + 46);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("Generated by One Time Success - onetimesuccess.com", margin, pageHeight - 32);

  return doc.output("blob");
}
